import { useMemo, useState } from "react";
import { LuCopy, LuCheck } from "react-icons/lu";
import type { SourceSnapshot, Item } from "@tuit/shared-types";
import { ItemRow } from "./ItemRow.js";
import { useViewSettings } from "../state/viewSettingsState.js";
import styles from "../styles/components/FlatView.module.css";

interface FlatItem {
  item: Item;
  sourceId: string;
  sourceTitle: string;
  domain: string | null;
  iconBackground?: string;
  faviconUrl?: string | null;
}

interface Props {
  sections: SourceSnapshot[];
  readIds: Set<string>;
  onRead: (id: string) => void;
  // item ids that are duplicates of an earlier item in the same room snapshot — excluded from display to avoid showing the same article twice
  suppressed: Set<string>;
  highlight?: string;
  mobile?: boolean;
  roomTitle?: string;
  roomUrl?: string;
}

export function FlatView({ sections, readIds, onRead, suppressed, highlight, mobile = false, roomTitle, roomUrl }: Props) {
  const { hideRead } = useViewSettings();
  const [copied, setCopied] = useState(false);
  const items = useMemo<FlatItem[]>(() => {
    const flat: FlatItem[] = [];
    for (const section of sections) {
      for (const item of section.items) {
        if (suppressed.has(item.id)) continue;
        if (hideRead && readIds.has(item.id)) continue;
        flat.push({
          item,
          sourceId: section.sourceId,
          sourceTitle: section.title,
          domain: item.domain ?? null,
          iconBackground: section.iconBackground,
          faviconUrl: section.faviconUrl,
        });
      }
    }
    flat.sort((a, b) => new Date(b.item.publishedAt).getTime() - new Date(a.item.publishedAt).getTime());
    return flat;
  }, [sections, readIds, hideRead, suppressed]);

  if (items.length === 0) {
    return <div className={styles.empty}>No unread items.</div>;
  }

  const handleCopyUrl = async () => {
    if (roomUrl) {
      await navigator.clipboard.writeText(roomUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={styles.container}>
      {roomTitle && (
        <div className={styles.header}>
          <h2 className={styles.title}>{roomTitle}</h2>
          {roomUrl && (
            <button
              onClick={handleCopyUrl}
              className={styles.copyButton}
              title="Copy room URL"
              aria-label="Copy room URL"
            >
              {copied ? <LuCheck size={18} /> : <LuCopy size={18} />}
            </button>
          )}
        </div>
      )}
      <div className={styles.list} style={mobile ? { columns: 1 } : undefined}>
        {items.map(({ item, sourceTitle, domain, iconBackground, faviconUrl }) => (
          <div key={item.id} className={styles.entry}>
            <ItemRow
              item={item}
              isRead={readIds.has(item.id)}
              onRead={onRead}
              source={{ title: sourceTitle, domain, iconBackground, faviconUrl }}
              highlight={highlight}
              mobile={mobile}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
